import {
  Box,
  TextInput,
  SimpleGrid,
  Text,
  Divider,
  Flex,
  Button,
  FileButton,
  Anchor,
  Paper,
  Select,
  Tooltip,
} from "@mantine/core";
import "react-international-phone/style.css";
import { PhoneInput } from "react-international-phone";
import { IconFileText, IconUpload, IconTrash, IconDownload, IconLock } from "@tabler/icons-react";
import { useEffect, useState } from "react";
import { errorNotification } from "@/shared/utils/errorNotification";
import trpc from "@/shared/utils/trpc";
import {
  getVerificationDocKind,
  VERIFICATION_BUSINESS_TYPE_SELECT_DATA,
} from "./verificationDocuments";

interface PersonalDetailsProps {
  formData: {
    userName: string;
    userDesignation: string;
    userPhoneNumber: string;
    userPhoneCountryCode: string;
    companyEmail: string;
    verificationBusinessType?: string | null;
  };
  updateForm: (field: string, value: any) => void;
  onVerificationBusinessTypeChange: (value: string | null) => void;
  isFieldLocked?: (fieldName: string) => boolean;
  mandatoryData?: {
    id?: string;
    name: string;
    file: File | null;
    url: string;
    existingUrl?: string;
  };
  authorityLetterData?: {
    id?: string;
    name: string;
    file: File | null;
    existingUrl?: string;
  };
  onUpdateMandatory?: (field: string, value: any) => void;
  onUpdateAuthorityLetter?: (field: string, value: any) => void;
  onRemoveMandatoryDoc?: () => void;
  onRemoveAuthorityLetterDoc?: () => void;
  isViewMode?: boolean;
  isEditMode?: boolean;
  companyStatus?: string | null;
}

export default function PersonalDetails({
  formData,
  updateForm,
  onVerificationBusinessTypeChange,
  isFieldLocked = () => false,
  mandatoryData,
  authorityLetterData,
  onUpdateMandatory,
  onUpdateAuthorityLetter,
  onRemoveMandatoryDoc,
  onRemoveAuthorityLetterDoc,
  isViewMode = false,
  isEditMode = false,
  companyStatus,
}: PersonalDetailsProps) {
  const [authorityLetterTemplateUrl, setAuthorityLetterTemplateUrl] = useState<string>("");
  const [affidavitTemplateUrl, setAffidavitTemplateUrl] = useState<string>("");

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const authority = await trpc.getTemplate.authorityLetter.query();
        setAuthorityLetterTemplateUrl(authority.url || "");
      } catch {
        setAuthorityLetterTemplateUrl("");
      }

      try {
        const affidavit = await trpc.getTemplate.affidavit.query();
        setAffidavitTemplateUrl(affidavit.url || "");
      } catch {
        setAffidavitTemplateUrl("");
      }
    };

    fetchTemplates();
  }, []);

  const showDocs = mandatoryData !== undefined || authorityLetterData !== undefined;
  // Can upload/remove only in create mode OR when company is blocked (ACTION_REQUIRED)
  const canEditMandatory = !isViewMode && (!isEditMode || companyStatus === "ACTION_REQUIRED");

  // If one doc is already uploaded from DB, only show that one
  // Private files may have empty url — use id as the primary indicator of existence
  const hasExistingAuthority = !!(authorityLetterData?.id || authorityLetterData?.existingUrl);
  const hasExistingAffidavit = !!(mandatoryData?.id || mandatoryData?.existingUrl);
  const selectedKind = getVerificationDocKind(formData.verificationBusinessType ?? null);
  const legacyBothDocs = !selectedKind && hasExistingAffidavit && hasExistingAuthority;

  const showAuthorityLetterSection =
    authorityLetterData !== undefined &&
    (selectedKind === "authority" || (!selectedKind && hasExistingAuthority));
  const showAffidavitSection =
    mandatoryData !== undefined && (selectedKind === "affidavit" || (!selectedKind && hasExistingAffidavit));
  const showOrDivider = showAuthorityLetterSection && showAffidavitSection && legacyBothDocs;

  const handleAuthorityLetterUpload = (file: File | null) => {
    if (!file || !onUpdateAuthorityLetter) return;
    if (file.size > 10 * 1024 * 1024) {
      errorNotification({ title: "File too large", message: "Maximum file size is 10MB." });
      return;
    }
    onUpdateAuthorityLetter("file", file);
  };

  const handleAffidavitUpload = (file: File | null) => {
    if (!file || !onUpdateMandatory) return;
    if (file.size > 10 * 1024 * 1024) {
      errorNotification({ title: "File too large", message: "Maximum file size is 10MB." });
      return;
    }
    onUpdateMandatory("file", file);
  };

  const formatDate = (date: any) =>
    new Date(date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });

  const lockedTooltip = "This field cannot be edited after verification";

  return (
    <Box>
      <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md" mb="md">
        {/* Name */}
        <TextInput
          label={
            <Text fz="sm" fw={500}>
              Name{" "}
              <Text span c="red" fz="lg">
                *
              </Text>
            </Text>
          }
          placeholder="Enter your name"
          value={formData.userName}
          onChange={(e) => updateForm("userName", e.currentTarget.value)}
        />

        {/* Designation */}
        <TextInput
          label={
            <Text fz="sm" fw={500}>
              Designation{" "}
              <Text span c="red" fz="lg">
                *
              </Text>
            </Text>
          }
          placeholder="Enter your designation"
          value={formData.userDesignation}
          onChange={(e) => updateForm("userDesignation", e.currentTarget.value)}
        />

        {/* Phone Number */}
        <TextInput
          label={
            <Text fz="sm" fw={500}>
              Phone Number
            </Text>
          }
          placeholder="hidden"
          styles={{
            input: { display: "none" },
            wrapper: { display: "none" },
            root: { marginBottom: 0 },
          }}
          inputContainer={() => (
            <PhoneInput
              defaultCountry="in"
              value={`${formData.userPhoneCountryCode}${formData.userPhoneNumber}`}
              onChange={(phone, meta) => {
                const countryCode = meta.country?.dialCode ? `+${meta.country.dialCode}` : "";
                const nationalNumber = phone.replace(`+${meta.country?.dialCode || ""}`, "").trim();
                updateForm("userPhoneCountryCode", countryCode);
                updateForm("userPhoneNumber", nationalNumber);
              }}
              placeholder="123-456-7890"
              style={{ width: "100%" }}
              inputStyle={{
                width: "100%",
                height: "36px",
                fontSize: "14px",
                border: "1px solid var(--mantine-color-gray-3)",
                borderLeft: "none",
                borderRadius: "0 var(--mantine-radius-md) var(--mantine-radius-md) 0",
                outline: "none",
              }}
              countrySelectorStyleProps={{
                buttonStyle: {
                  height: "36px",
                  border: "1px solid var(--mantine-color-gray-3)",
                  borderRadius: "var(--mantine-radius-md) 0 0 var(--mantine-radius-md)",
                  padding: "0 12px",
                  backgroundColor: "white",
                },
              }}
            />
          )}
        />

        {/* Company Email (same field as company profile; shown here for context) */}
        <TextInput
          label={
            <Flex align="center" gap={4}>
              <Text fz="sm" fw={500}>
                Company Email{" "}
                <Text span c="red" fz="lg">
                  *
                </Text>
              </Text>
              {isFieldLocked("companyEmail") && (
                <Tooltip label={lockedTooltip} withArrow position="top">
                  <Flex align="center">
                    <IconLock size={12} color="#9CA3AF" />
                  </Flex>
                </Tooltip>
              )}
            </Flex>
          }
          placeholder="contact@company.com"
          value={formData.companyEmail}
          onChange={(e) => updateForm("companyEmail", e.currentTarget.value)}
          error={
            formData.companyEmail && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.companyEmail)
              ? "Please enter a valid email address"
              : null
          }
          disabled={isFieldLocked("companyEmail") || true}
        />
      </SimpleGrid>

      {showDocs && (
        <>
          <Divider my="xl" />

          <Text fz="sm" fw={500} mb={4}>
            Verification Documents{" "}
            <Text span c="red" fz="lg">
              *
            </Text>
          </Text>
          <Text fz="xs" c="slate.6" mb="md">
            Select your business structure, then upload the document required for verification.
          </Text>

          <Select
            label={
              <Text fz="sm" fw={500}>
                Business structure{" "}
                <Text span c="red" fz="lg">
                  *
                </Text>
              </Text>
            }
            placeholder="Select an option"
            data={VERIFICATION_BUSINESS_TYPE_SELECT_DATA}
            value={formData.verificationBusinessType ?? null}
            onChange={onVerificationBusinessTypeChange}
            clearable
            mb="lg"
            disabled={isViewMode || (isEditMode && !canEditMandatory)}
          />

          {/* Authority Letter */}
          {showAuthorityLetterSection && (
            <Box mb="sm">
              <Flex justify="space-between" align="center" mb="xs">
                <Flex align="center" gap="xs">
                  <IconFileText size={16} color="var(--mantine-color-slate-6)" />
                  <Text fz="sm" fw={500}>
                    Authority Letter
                  </Text>
                </Flex>
                <Flex align="center" gap="xs">
                  <Button
                    component="a"
                    href={authorityLetterTemplateUrl || undefined}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="xs"
                    variant="light"
                    leftSection={<IconDownload size={13} />}
                    disabled={!authorityLetterTemplateUrl}
                  >
                    Download
                  </Button>
                </Flex>
              </Flex>
              <Text fz="xs" c="slate.6" mb="sm">
                Company-issued document authorizing an individual to represent and act on behalf of the company.
              </Text>

              {(authorityLetterData!.id || authorityLetterData!.existingUrl) && !authorityLetterData!.file ? (
                // Existing doc from DB — Paper card
                <Paper withBorder p="md" radius="md" style={{ borderColor: "#E5E7EB" }}>
                  <Flex justify="space-between" align="center">
                    <Flex align="center" gap="sm">
                      <IconFileText size={28} color="#2563EB" />
                      <Box>
                        <Text fw={500} fz="sm">
                          {authorityLetterData!.name}
                        </Text>
                        {(authorityLetterData as any).createdAt && (
                          <Text fz="xs" c="slate.6" mt={2}>
                            Uploaded {formatDate((authorityLetterData as any).createdAt)}
                          </Text>
                        )}
                        {authorityLetterData!.existingUrl && (
                          <Anchor href={authorityLetterData!.existingUrl} target="_blank" fz="xs">
                            View Document
                          </Anchor>
                        )}
                      </Box>
                    </Flex>
                    {canEditMandatory && onRemoveAuthorityLetterDoc && (
                      <Button
                        size="xs"
                        variant="subtle"
                        color="red"
                        leftSection={<IconTrash size={14} />}
                        onClick={onRemoveAuthorityLetterDoc}
                      >
                        Remove
                      </Button>
                    )}
                  </Flex>
                </Paper>
              ) : authorityLetterData!.file ? (
                // New file selected — Paper card
                <Paper withBorder p="md" radius="md" style={{ borderColor: "#E5E7EB" }}>
                  <Flex justify="space-between" align="center">
                    <Flex align="center" gap="sm">
                      <IconFileText size={28} color="#2563EB" />
                      <Text fw={500} fz="sm">
                        {authorityLetterData!.file.name}
                      </Text>
                    </Flex>
                    {canEditMandatory && onRemoveAuthorityLetterDoc && (
                      <Button
                        size="xs"
                        variant="subtle"
                        color="red"
                        leftSection={<IconTrash size={14} />}
                        onClick={onRemoveAuthorityLetterDoc}
                      >
                        Remove
                      </Button>
                    )}
                  </Flex>
                </Paper>
              ) : canEditMandatory ? (
                // Empty — Paper card with Upload button at end
                <Paper withBorder p="md" radius="md" style={{ borderColor: "#E5E7EB" }}>
                  <Flex justify="space-between" align="center">
                    <Flex align="center" gap="sm">
                      <IconFileText size={28} color="var(--mantine-color-slate-4)" />
                      <Box>
                        <Text fw={500} fz="sm">No document uploaded</Text>
                        <Text fz="xs" c="slate.6">Upload a signed authority letter</Text>
                      </Box>
                    </Flex>
                    <FileButton onChange={handleAuthorityLetterUpload} accept=".pdf,.doc,.docx">
                      {(props) => (
                        <Button size="xs" variant="outline" leftSection={<IconUpload size={13} />} {...props}>
                          Upload
                        </Button>
                      )}
                    </FileButton>
                  </Flex>
                </Paper>
              ) : (
                <Text fz="sm" c="slate.5">
                  Not provided
                </Text>
              )}
            </Box>
          )}

          {/* OR Divider — only when both sections are shown */}
          {showOrDivider && (
            <Flex align="center" gap="sm" my="sm">
              <Divider style={{ flex: 1 }} />
              <Text fz="xs" c="slate.5" fw={500}>
                OR
              </Text>
              <Divider style={{ flex: 1 }} />
            </Flex>
          )}

          {/* Affidavit */}
          {showAffidavitSection && (
            <Box mt="sm">
              <Flex justify="space-between" align="center" mb="xs">
                <Flex align="center" gap="xs">
                  <IconFileText size={16} color="var(--mantine-color-slate-6)" />
                  <Text fz="sm" fw={500}>
                    Affidavit
                  </Text>
                </Flex>
                <Flex align="center" gap="xs">
                  <Button
                    component="a"
                    href={affidavitTemplateUrl || undefined}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="xs"
                    variant="light"
                    leftSection={<IconDownload size={13} />}
                    disabled={!affidavitTemplateUrl}
                  >
                    Download
                  </Button>
                </Flex>
              </Flex>
              <Text fz="xs" c="slate.6" mb="sm">
                Self-declaration, confirming ownership and authority to represent the business.
              </Text>

              {(mandatoryData!.id || mandatoryData!.existingUrl) && !mandatoryData!.file ? (
                // Existing doc from DB — Paper card
                <Paper withBorder p="md" radius="md" style={{ borderColor: "#E5E7EB" }}>
                  <Flex justify="space-between" align="center">
                    <Flex align="center" gap="sm">
                      <IconFileText size={28} color="#2563EB" />
                      <Box>
                        <Text fw={500} fz="sm">
                          {mandatoryData!.name}
                        </Text>
                        {(mandatoryData as any).createdAt && (
                          <Text fz="xs" c="slate.6" mt={2}>
                            Uploaded {formatDate((mandatoryData as any).createdAt)}
                          </Text>
                        )}
                        {mandatoryData!.existingUrl && (
                          <Anchor href={mandatoryData!.existingUrl} target="_blank" fz="xs">
                            View Document
                          </Anchor>
                        )}
                      </Box>
                    </Flex>
                    {canEditMandatory && onRemoveMandatoryDoc && (
                      <Button
                        size="xs"
                        variant="subtle"
                        color="red"
                        leftSection={<IconTrash size={14} />}
                        onClick={onRemoveMandatoryDoc}
                      >
                        Remove
                      </Button>
                    )}
                  </Flex>
                </Paper>
              ) : mandatoryData!.file ? (
                // New file selected — Paper card
                <Paper withBorder p="md" radius="md" style={{ borderColor: "#E5E7EB" }}>
                  <Flex justify="space-between" align="center">
                    <Flex align="center" gap="sm">
                      <IconFileText size={28} color="#2563EB" />
                      <Text fw={500} fz="sm">
                        {mandatoryData!.file.name}
                      </Text>
                    </Flex>
                    {canEditMandatory && onRemoveMandatoryDoc && (
                      <Button
                        size="xs"
                        variant="subtle"
                        color="red"
                        leftSection={<IconTrash size={14} />}
                        onClick={onRemoveMandatoryDoc}
                      >
                        Remove
                      </Button>
                    )}
                  </Flex>
                </Paper>
              ) : canEditMandatory ? (
                // Empty — Paper card with Upload button at end
                <Paper withBorder p="md" radius="md" style={{ borderColor: "#E5E7EB" }}>
                  <Flex justify="space-between" align="center">
                    <Flex align="center" gap="sm">
                      <IconFileText size={28} color="var(--mantine-color-slate-4)" />
                      <Box>
                        <Text fw={500} fz="sm">No document uploaded</Text>
                        <Text fz="xs" c="slate.6">Upload a signed affidavit</Text>
                      </Box>
                    </Flex>
                    <FileButton onChange={handleAffidavitUpload} accept=".pdf,.doc,.docx">
                      {(props) => (
                        <Button size="xs" variant="outline" leftSection={<IconUpload size={13} />} {...props}>
                          Upload
                        </Button>
                      )}
                    </FileButton>
                  </Flex>
                </Paper>
              ) : (
                <Text fz="sm" c="slate.5">
                  Not provided
                </Text>
              )}
            </Box>
          )}
        </>
      )}
    </Box>
  );
}
